import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';
import { Copy, Check, Code, ExternalLink, Info, Globe, Palette, Eye } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || window.location.origin;

// Plan tiers with branding info
const PLAN_TIERS = {
  free: { name: 'Free', branding: true },
  starter: { name: 'Starter', branding: false },
  professional: { name: 'Professional', branding: false },
  business: { name: 'Business', branding: false },
};

const DoctorEmbed = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Doctor data state
  const [doctorId, setDoctorId] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Customization state
  const [primaryColor, setPrimaryColor] = useState('#0f172a');
  const [secondaryColor, setSecondaryColor] = useState('#94A3B8');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [fontFamily, setFontFamily] = useState('Inter');
  const [showBranding, setShowBranding] = useState(true);
  const [widgetWidth, setWidgetWidth] = useState('100%');
  const [widgetHeight, setWidgetHeight] = useState('600');

  // Font options for the dropdown
  const fontOptions = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins',
    'Source Sans Pro',
    'Nunito',
    'Raleway',
    'PT Sans'
  ];

  // Copy state
  const [copiedIframe, setCopiedIframe] = useState(false);
  const [copiedJs, setCopiedJs] = useState(false);

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch doctor data and subscription
  const fetchDoctorData = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'doctor' || !token) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get doctor ID from auth
      const meResponse = await fetchWithTimeout(
        `${API_URL}/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!meResponse.ok) {
        throw new Error('Failed to fetch user data');
      }

      const meData = await meResponse.json();
      setDoctorId(meData.doctorId);

      // Fetch subscription to check branding permissions
      const subResponse = await fetchWithTimeout(
        `${API_URL}/subscriptions/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData);

        // Set branding based on plan tier
        const planTier = subData.planTier || 'free';
        const planInfo = PLAN_TIERS[planTier] || PLAN_TIERS.free;
        setShowBranding(planInfo.branding);
      }
    } catch (error) {
      console.error('Error fetching doctor data:', error);
      if (isTimeoutError(error)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(error)) {
        setError('Unable to connect to the server.');
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, token]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'doctor' && token) {
      fetchDoctorData();
    }
  }, [isAuthenticated, user, token, fetchDoctorData]);

  // Check if user can remove branding
  const canRemoveBranding = subscription &&
    PLAN_TIERS[subscription.planTier]?.branding === false;

  // Generate embed URL with customization parameters
  const generateEmbedUrl = () => {
    if (!doctorId) return '';

    const params = new URLSearchParams();
    params.set('embed', 'true');
    params.set('primaryColor', primaryColor.replace('#', ''));
    params.set('secondaryColor', secondaryColor.replace('#', ''));
    params.set('bgColor', backgroundColor.replace('#', ''));
    params.set('fontFamily', encodeURIComponent(fontFamily));
    if (!showBranding && canRemoveBranding) {
      params.set('noBranding', 'true');
    }

    return `${FRONTEND_URL}/doctor/${doctorId}?${params.toString()}`;
  };

  // Generate iframe embed code
  const generateIframeCode = () => {
    const embedUrl = generateEmbedUrl();
    return `<iframe
  src="${embedUrl}"
  width="${widgetWidth}"
  height="${widgetHeight}px"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  allow="payment"
  title="Book an appointment"
></iframe>`;
  };

  // Generate JavaScript embed code
  const generateJsCode = () => {
    const embedUrl = generateEmbedUrl();
    return `<!-- Calendiary Booking Widget -->
<div id="calendiary-widget"></div>
<script>
(function() {
  var container = document.getElementById('calendiary-widget');
  var iframe = document.createElement('iframe');
  iframe.src = '${embedUrl}';
  iframe.width = '${widgetWidth}';
  iframe.height = '${widgetHeight}px';
  iframe.frameBorder = '0';
  iframe.style.border = 'none';
  iframe.style.borderRadius = '8px';
  iframe.allow = 'payment';
  iframe.title = 'Book an appointment';
  container.appendChild(iframe);
})();
</script>`;
  };

  // Copy to clipboard handlers
  const copyToClipboard = async (text, setCopied) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with breadcrumb */}
        <div className="mb-8">
          <Link
            to="/doctor/dashboard"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Embed Widget
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Add a booking widget to your website so patients can book appointments directly
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400 font-body">{error}</p>
            <button
              onClick={fetchDoctorData}
              className="mt-2 font-mono text-xs uppercase tracking-widest text-red-400 hover:text-red-300 transition-smooth"
            >
              Try Again
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Customization & Code */}
          <div className="space-y-6">
            {/* Customization Options */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Customize Widget
              </h2>

              <div className="space-y-4">
                {/* Primary Color */}
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={primaryColor.startsWith('#') ? primaryColor : `#${primaryColor}`}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-12 h-10 rounded-lg cursor-pointer border border-light-border dark:border-border"
                    />
                    <input
                      type="text"
                      value={primaryColor}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Ensure value starts with # if it's a valid hex color
                        if (value && !value.startsWith('#') && /^[0-9A-Fa-f]+$/.test(value)) {
                          value = `#${value}`;
                        }
                        setPrimaryColor(value);
                      }}
                      className="flex-1 px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                      placeholder="#0f172a"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={secondaryColor.startsWith('#') ? secondaryColor : `#${secondaryColor}`}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="w-12 h-10 rounded-lg cursor-pointer border border-light-border dark:border-border"
                    />
                    <input
                      type="text"
                      value={secondaryColor}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Ensure value starts with # if it's a valid hex color
                        if (value && !value.startsWith('#') && /^[0-9A-Fa-f]+$/.test(value)) {
                          value = `#${value}`;
                        }
                        setSecondaryColor(value);
                      }}
                      className="flex-1 px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                      placeholder="#94A3B8"
                    />
                  </div>
                  <p className="text-xs text-slate-400 dark:text-silver/40 mt-1">
                    Used for borders and secondary elements
                  </p>
                </div>

                {/* Background Color */}
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                    Background Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={backgroundColor.startsWith('#') ? backgroundColor : `#${backgroundColor}`}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-12 h-10 rounded-lg cursor-pointer border border-light-border dark:border-border"
                    />
                    <input
                      type="text"
                      value={backgroundColor}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Ensure value starts with # if it's a valid hex color
                        if (value && !value.startsWith('#') && /^[0-9A-Fa-f]+$/.test(value)) {
                          value = `#${value}`;
                        }
                        setBackgroundColor(value);
                      }}
                      className="flex-1 px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                {/* Font Family */}
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                    Font Family
                  </label>
                  <select
                    value={fontFamily}
                    onChange={(e) => setFontFamily(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                    style={{ fontFamily: fontFamily }}
                  >
                    {fontOptions.map((font) => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 dark:text-silver/40 mt-1">
                    Applied to all text in the widget
                  </p>
                </div>

                {/* Widget Dimensions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                      Width
                    </label>
                    <input
                      type="text"
                      value={widgetWidth}
                      onChange={(e) => setWidgetWidth(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                      placeholder="100%"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                      Height (px)
                    </label>
                    <input
                      type="text"
                      value={widgetHeight}
                      onChange={(e) => setWidgetHeight(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-sm focus:border-silver/50 focus:outline-none transition-smooth"
                      placeholder="600"
                    />
                  </div>
                </div>

                {/* Branding Toggle */}
                <div className="pt-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-body text-sm text-light-text dark:text-pure-white">
                        Show Calendiary Branding
                      </label>
                      <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-1">
                        {canRemoveBranding
                          ? 'You can remove branding with your paid plan'
                          : 'Upgrade to remove branding'}
                      </p>
                    </div>
                    <button
                      onClick={() => canRemoveBranding && setShowBranding(!showBranding)}
                      disabled={!canRemoveBranding}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        showBranding
                          ? 'bg-silver'
                          : 'bg-slate-300 dark:bg-charcoal'
                      } ${!canRemoveBranding ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          showBranding ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                  {!canRemoveBranding && (
                    <Link
                      to="/doctor/subscription"
                      className="inline-flex items-center gap-1 mt-2 font-mono text-xs uppercase tracking-widest text-silver hover:text-pure-white transition-smooth"
                    >
                      Upgrade Plan <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Iframe Embed Code */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Iframe Embed Code
                </h2>
                <button
                  onClick={() => copyToClipboard(generateIframeCode(), setCopiedIframe)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
                >
                  {copiedIframe ? (
                    <>
                      <Check className="w-3 h-3 text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-slate-100 dark:bg-obsidian border border-light-border dark:border-border rounded-xl p-4 overflow-x-auto">
                <code className="font-mono text-xs text-light-text dark:text-silver whitespace-pre-wrap break-all">
                  {generateIframeCode()}
                </code>
              </pre>
            </div>

            {/* JavaScript Embed Code */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  JavaScript Embed Code
                </h2>
                <button
                  onClick={() => copyToClipboard(generateJsCode(), setCopiedJs)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
                >
                  {copiedJs ? (
                    <>
                      <Check className="w-3 h-3 text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-slate-100 dark:bg-obsidian border border-light-border dark:border-border rounded-xl p-4 overflow-x-auto">
                <code className="font-mono text-xs text-light-text dark:text-silver whitespace-pre-wrap break-all">
                  {generateJsCode()}
                </code>
              </pre>
            </div>
          </div>

          {/* Right Column - Preview & Instructions */}
          <div className="space-y-6">
            {/* Live Preview */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Live Preview
              </h2>
              <div
                className="border border-light-border dark:border-border rounded-xl overflow-hidden"
                style={{ backgroundColor }}
              >
                {doctorId ? (
                  <iframe
                    src={generateEmbedUrl()}
                    width="100%"
                    height="400px"
                    frameBorder="0"
                    style={{ border: 'none' }}
                    title="Widget Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-light-text-secondary dark:text-silver/60">
                    <p className="font-body text-sm">Loading preview...</p>
                  </div>
                )}
              </div>
              <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-3 text-center">
                Preview shows how the widget will appear on your website
              </p>
            </div>

            {/* Platform Instructions */}
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Platform Instructions
              </h2>

              <div className="space-y-4">
                {/* WordPress */}
                <div className="border border-light-border dark:border-border rounded-xl p-4">
                  <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                    WordPress
                  </h3>
                  <ol className="list-decimal list-inside font-body text-sm text-light-text-secondary dark:text-silver/60 space-y-1">
                    <li>Edit the page where you want the widget</li>
                    <li>Add a "Custom HTML" block</li>
                    <li>Paste the iframe or JavaScript code</li>
                    <li>Save and publish your page</li>
                  </ol>
                </div>

                {/* Wix */}
                <div className="border border-light-border dark:border-border rounded-xl p-4">
                  <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                    Wix
                  </h3>
                  <ol className="list-decimal list-inside font-body text-sm text-light-text-secondary dark:text-silver/60 space-y-1">
                    <li>Open your Wix Editor</li>
                    <li>Click "Add" → "Embed" → "HTML iframe"</li>
                    <li>Paste the iframe code in the "Add your code" field</li>
                    <li>Resize the element as needed</li>
                  </ol>
                </div>

                {/* Squarespace */}
                <div className="border border-light-border dark:border-border rounded-xl p-4">
                  <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                    Squarespace
                  </h3>
                  <ol className="list-decimal list-inside font-body text-sm text-light-text-secondary dark:text-silver/60 space-y-1">
                    <li>Edit your page and click "Add Block"</li>
                    <li>Select "Code" under "More"</li>
                    <li>Paste the JavaScript embed code</li>
                    <li>Toggle "Display Source" off and save</li>
                  </ol>
                </div>

                {/* Custom HTML */}
                <div className="border border-light-border dark:border-border rounded-xl p-4">
                  <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                    Custom HTML Website
                  </h3>
                  <ol className="list-decimal list-inside font-body text-sm text-light-text-secondary dark:text-silver/60 space-y-1">
                    <li>Open your HTML file in a text editor</li>
                    <li>Find where you want the widget to appear</li>
                    <li>Paste either the iframe or JavaScript code</li>
                    <li>Save and upload your file</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Help Note */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-sm text-blue-200">
                  <strong>Need help?</strong> The iframe code works on most platforms.
                  Use the JavaScript code if you need more control over when the widget loads.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorEmbed;
